import {
  Conversation,
  LBBCompany,
  LBBContact,
  SchedulerCalendarEvent,
  UniqueVisitor,
  VisitorSession,
} from 'sb-lib/types';

export enum AppNotificationType {
  visitor_alert = 'visitor_alert',
  visitor_call = 'visitor_call',
  visitor_meeting_booked = 'visitor_meeting_booked',
  visitor_contact_created = 'visitor_contact_created',
  visitor_conversation_update = 'visitor_conversation_update',
  company_created = 'company_created',
  sixsense_fetches_cap_exceeded = 'sixsense_fetches_cap_exceeded',
  visitor_ai_chat_handoff = 'visitor_ai_chat_handoff',
}

export interface AppNotification {
  id: number;
  dateCreated: string;
  dateRead: string;
  notificationType: AppNotificationType;

  // FE generated fields
  popupExpiration?: number;
  isPopupExpired?: boolean;
  popupDuration?: number;
  isOptimisticDelete?: boolean;
}

type ANContact = Omit<LBBContact, 'company' | 'alert'>;
type ANCompany = Omit<LBBCompany, 'contactsCount'>;
type ANVisitorSession = Omit<VisitorSession, 'sessionActions'>;
type ANSchedulerCalendarEvent = Omit<
  SchedulerCalendarEvent,
  'userAvailability' | 'eventType' | 'sortedAtendees'
>;
type ANVisitor = Omit<
  UniqueVisitor,
  'contact' | 'customMetadata' | 'blocks' | 'displayName' | 'lastSeen'
>;
type ANConversation = Omit<
  Conversation,
  'assignee' | 'closedBy' | 'visitor' | 'lastAgentId'
>;

export interface AppNotificationVisitorAlert extends AppNotification {
  notificationType: AppNotificationType.visitor_alert;
  data: {
    title: string;
    body: string;
    directUserIds: [number];
    routedUserIds: [number];
    routingRuleName?: string;
    routingRuleId?: number;
  };
  visitor: ANVisitor;
  contact?: ANContact;
  company?: ANCompany;
}

export interface AppNotificationVisitorCall extends AppNotification {
  notificationType: AppNotificationType.visitor_call;
  data: {
    routedUserIds: [number];
    routingRuleName?: string;
    routingRuleId?: number;
  };
  session: ANVisitorSession;
  visitor: ANVisitor;
  contact?: ANContact;
  company?: ANCompany;
}

export interface AppNotificationVisitorMeetingBooked extends AppNotification {
  notificationType: AppNotificationType.visitor_meeting_booked;
  data: {};
  calendarEvent: ANSchedulerCalendarEvent;
  visitor?: ANVisitor;
}

export interface AppNotificationVisitorContactCreated extends AppNotification {
  notificationType: AppNotificationType.visitor_contact_created;
  data: {};
  contact: ANContact;
}

export interface AppNotificationVisitorConversationUpdate extends AppNotification {
  notificationType: AppNotificationType.visitor_conversation_update;
  data: {};
  conversation: ANConversation;
  visitor: ANVisitor;
  contact?: ANContact;
}

export interface AppNotificationCompanyCreated extends AppNotification {
  notificationType: AppNotificationType.company_created;
  data: {};
  company: ANCompany;
  visitor?: ANVisitor;
  contact?: ANContact;
}

export interface AppNotificationSixsenseFetchesCapExceeded extends AppNotification {
  notificationType: AppNotificationType.sixsense_fetches_cap_exceeded;
  data: {
    plan_allowance: number;
  };
  company: ANCompany;
  visitor?: ANVisitor;
  contact?: ANContact;
}

export interface AppNotificationVisitorAiChatHandoff extends AppNotification {
  notificationType: AppNotificationType.visitor_ai_chat_handoff;
  conversation: ANConversation;
  visitor: ANVisitor;
  contact?: ANContact;
  company?: ANCompany;
}

// Type -> Interface mapping
interface AppNotificationByType {
  [AppNotificationType.visitor_alert]: AppNotificationVisitorAlert;
  [AppNotificationType.visitor_call]: AppNotificationVisitorCall;
  [AppNotificationType.visitor_meeting_booked]: AppNotificationVisitorMeetingBooked;
  [AppNotificationType.visitor_contact_created]: AppNotificationVisitorContactCreated;
  [AppNotificationType.visitor_conversation_update]: AppNotificationVisitorConversationUpdate;
  [AppNotificationType.company_created]: AppNotificationCompanyCreated;
  [AppNotificationType.sixsense_fetches_cap_exceeded]: AppNotificationSixsenseFetchesCapExceeded;
  [AppNotificationType.visitor_ai_chat_handoff]: AppNotificationVisitorAiChatHandoff;
}

// Type helpers (mainly type-narrowing)

// Narrow to base class
export const isAppNotification = (x: any): x is AppNotification => {
  return (
    'id' in x &&
    'dateCreated' in x &&
    'dateRead' in x &&
    'notificationType' in x &&
    Object.values(AppNotificationType).includes(x.notificationType)
  );
};

// Narrow to specific type
export const isAppNotificationByType = <T extends AppNotificationType>(
  t: T,
  x: any,
): x is AppNotificationByType[T] => {
  return isAppNotification(x) && x.notificationType === t;
};

export const isAppNotificationVisitorAlert = (x: any): x is AppNotificationVisitorAlert =>
  isAppNotificationByType(AppNotificationType.visitor_alert, x);

export const isAppNotificationVisitorCall = (x: any): x is AppNotificationVisitorCall =>
  isAppNotificationByType(AppNotificationType.visitor_call, x);

export const isAppNotificationVisitorMeetingBooked = (
  x: any,
): x is AppNotificationVisitorMeetingBooked =>
  isAppNotificationByType(AppNotificationType.visitor_meeting_booked, x);

export const isAppNotificationVisitorContactCreated = (
  x: any,
): x is AppNotificationVisitorContactCreated =>
  isAppNotificationByType(AppNotificationType.visitor_contact_created, x);

export const isAppNotificationVisitorConversationUpdate = (
  x: any,
): x is AppNotificationVisitorConversationUpdate =>
  isAppNotificationByType(AppNotificationType.visitor_conversation_update, x);

export const isAppNotificationCompanyCreated = (
  x: any,
): x is AppNotificationCompanyCreated =>
  isAppNotificationByType(AppNotificationType.company_created, x);

export const isAppNotificationSixsenseFetchesCapExceeded = (
  x: any,
): x is AppNotificationSixsenseFetchesCapExceeded =>
  isAppNotificationByType(AppNotificationType.sixsense_fetches_cap_exceeded, x);

export const isAppNotificationVisitorAiChatHandoff = (
  x: any,
): x is AppNotificationVisitorAiChatHandoff =>
  isAppNotificationByType(AppNotificationType.visitor_ai_chat_handoff, x);

// Narrow to having specific relationships

// Narrow to type with visitor relation
export type AppNotificationWithVisitor =
  | AppNotificationVisitorAlert
  | AppNotificationVisitorCall;
export const isAppNotificationWithVisitor = (x: any): x is AppNotificationWithVisitor => {
  return (
    isAppNotificationVisitorAlert(x) ||
    isAppNotificationVisitorCall(x) ||
    isAppNotificationVisitorAiChatHandoff(x)
  );
};

// Narrow to type with calendar event relation
export type AppNotificationWithCalendarEvent = AppNotificationVisitorMeetingBooked;
export const isAppNotificationWithCalendarEvent = (
  x: any,
): x is AppNotificationWithCalendarEvent => {
  return isAppNotificationVisitorMeetingBooked(x);
};

// Narrow to type with conversation relation
export type AppNotificationWithConversation = AppNotificationVisitorConversationUpdate;
export const isAppNotificationWithConversation = (
  x: any,
): x is AppNotificationWithConversation => {
  return isAppNotificationVisitorConversationUpdate(x);
};

// Narrow to type with company relation
export type AppNotificationWithCompany = AppNotificationCompanyCreated;
export const isAppNotificationWithCompany = (x: any): x is AppNotificationWithCompany => {
  return isAppNotificationCompanyCreated(x);
};

// Narrow to type with contact relation
export type AppNotificationWithContact =
  | AppNotificationVisitorAlert
  | AppNotificationVisitorCall;
export const isAppNotificationWithContact = (x: any): x is AppNotificationWithContact => {
  return isAppNotificationVisitorAlert(x) || isAppNotificationVisitorCall(x);
};

// Popup configs
export interface AppNotificationConfig {
  popupTimeout: number;
}
export const APP_NOTIFICATION_CONFIG: Record<
  AppNotificationType,
  AppNotificationConfig
> = {
  [AppNotificationType.visitor_alert]: {
    popupTimeout: 30000,
  },
  [AppNotificationType.visitor_call]: {
    popupTimeout: 30000,
  },
  [AppNotificationType.visitor_meeting_booked]: {
    popupTimeout: 30000,
  },
  [AppNotificationType.visitor_contact_created]: {
    popupTimeout: 30000,
  },
  [AppNotificationType.visitor_conversation_update]: {
    popupTimeout: 0, // never show popup for this
  },
  [AppNotificationType.company_created]: {
    popupTimeout: 30000,
  },
  [AppNotificationType.sixsense_fetches_cap_exceeded]: {
    popupTimeout: 30000,
  },
  [AppNotificationType.visitor_ai_chat_handoff]: {
    popupTimeout: 30000,
  },
};
